/*
  The replay slider (architecture 13.2). The server sends one history feed: a frame for each recorded change, with
  the subject, its state and its parent, plus markers at hand-overs, SME reviews and restores. The browser steps
  through it. The state at a time T is the last change at or before T (times never run backwards), so a frame
  is found by looking at the changes up to a cut-off. Nothing here talks to the server.
*/

import type { ElementKind, HistoryFeed, HistoryFrame, HistoryMarker, Id, Ts } from "../../../api/types";
import { buildNavModelFromNodes, type NavModel, type NavNode } from "./nav";
import { isGone, toDisplayState } from "./state";

/** The kinds of element the family navigator draws. */
export const NAVIGATOR_KINDS: ReadonlySet<ElementKind> = new Set<ElementKind>(["library", "family", "sub_family", "item"]);

/** A cut-off: up to a change number, or up to a time. */
export interface Cutoff {
  event?: number;
  at?: Ts;
}

export interface Step {
  event: number;
  at: Ts;
}

/** The frames in the order they happened. The server sends them so; a copy is sorted to be safe. */
export function orderedFrames(feed: HistoryFeed | null | undefined): HistoryFrame[] {
  const frames = feed?.frames ?? [];
  let sorted = true;
  for (let i = 1; i < frames.length; i += 1) {
    if ((frames[i - 1] as HistoryFrame).event > (frames[i] as HistoryFrame).event) {
      sorted = false;
      break;
    }
  }
  return sorted ? frames.slice() : frames.slice().sort((a, b) => a.event - b.event);
}

/** The places the slider can stop: one for each change number, oldest first. */
export function stepsOf(frames: readonly HistoryFrame[]): Step[] {
  const steps: Step[] = [];
  for (const frame of frames) {
    const last = steps[steps.length - 1];
    if (last && last.event === frame.event) continue;
    steps.push({ event: frame.event, at: frame.at });
  }
  return steps;
}

function within(frame: HistoryFrame, cutoff: Cutoff | null | undefined): boolean {
  if (!cutoff) return true;
  if (cutoff.event !== undefined && frame.event > cutoff.event) return false;
  if (cutoff.at !== undefined && Date.parse(frame.at) > Date.parse(cutoff.at)) return false;
  return true;
}

/** The frames up to a cut-off. */
export function framesUpTo(frames: readonly HistoryFrame[], cutoff: Cutoff | null | undefined): HistoryFrame[] {
  return frames.filter((frame) => within(frame, cutoff));
}

/** The last frame of each element at the cut-off. An element that was withdrawn or removed is not in it. */
export function stateAt(frames: readonly HistoryFrame[], cutoff: Cutoff | null | undefined): Map<Id, HistoryFrame> {
  const last = new Map<Id, HistoryFrame>();
  for (const frame of frames) {
    if (frame.subject_kind !== "element" || !within(frame, cutoff)) continue;
    const before = last.get(frame.subject_id);
    if (!before || frame.event >= before.event) last.set(frame.subject_id, frame);
  }
  for (const [id, frame] of last) if (isGone(frame.state)) last.delete(id);
  return last;
}

export interface ReplayCounts {
  validated: number;
  loaded: number;
  waiting: number;
  sent_back: number;
  rejected: number;
}

/** The counters of the discipline as they stood at the cut-off. Proposals and notes are not in the record. */
export function countsAt(frames: readonly HistoryFrame[], cutoff: Cutoff | null | undefined): ReplayCounts {
  const counts: ReplayCounts = { validated: 0, loaded: 0, waiting: 0, sent_back: 0, rejected: 0 };
  for (const frame of stateAt(frames, cutoff).values()) {
    const look = toDisplayState(frame.state, "loaded");
    if (look === "validated") counts.validated += 1;
    else if (look === "loaded") counts.loaded += 1;
    else if (look === "waiting") counts.waiting += 1;
    else if (look === "sent_back") counts.sent_back += 1;
    else if (look === "rejected") counts.rejected += 1;
  }
  return counts;
}

/** The family tree as it stood at the cut-off, made from the frames. What the live tree knows about a node is added. */
export function replayNodes(frames: readonly HistoryFrame[], cutoff: Cutoff | null | undefined, live?: NavModel | null): NavNode[] {
  const states = stateAt(frames, cutoff);

  // How many elements hang below each one, by the parent each recorded: the progress of a family or an item.
  const kids = new Map<Id, Id[]>();
  for (const [id, frame] of states) {
    if (!frame.parent_id || frame.parent_id === id || !states.has(frame.parent_id)) continue;
    const list = kids.get(frame.parent_id);
    if (list) list.push(id);
    else kids.set(frame.parent_id, [id]);
  }
  const tally = (id: Id, seen: Set<Id>): { validated: number; waiting: number; total: number } => {
    const out = { validated: 0, waiting: 0, total: 0 };
    for (const child of kids.get(id) ?? []) {
      if (seen.has(child)) continue;
      seen.add(child);
      const frame = states.get(child);
      if (frame && !NAVIGATOR_KINDS.has(frame.kind as ElementKind)) {
        const look = toDisplayState(frame.state, "loaded");
        out.total += 1;
        if (look === "validated") out.validated += 1;
        if (look === "waiting") out.waiting += 1;
      }
      const inner = tally(child, seen);
      out.validated += inner.validated;
      out.waiting += inner.waiting;
      out.total += inner.total;
    }
    return out;
  };

  const nodes: NavNode[] = [];
  for (const [id, frame] of states) {
    if (!NAVIGATOR_KINDS.has(frame.kind as ElementKind)) continue;
    const kind = frame.kind as ElementKind;
    const known = live?.byId.get(id);
    const progress = tally(id, new Set([id]));
    const hasItemBelow = (kids.get(id) ?? []).some((child) => states.get(child)?.kind === "item");
    nodes.push({
      id,
      label: frame.name,
      level: known?.level ?? (kind === "item" ? (hasItemBelow ? "main_parent" : "item") : kind),
      kind,
      parentId: frame.parent_id && states.has(frame.parent_id) ? frame.parent_id : null,
      state: toDisplayState(frame.state, "loaded"),
      origin: known?.origin ?? (frame.state ? "work" : "loaded"),
      abstract: known?.abstract ?? false,
      drawn: known?.drawn ?? true,
      progress: progress.total > 0 ? progress : null,
    });
  }
  return nodes;
}

export function replayModel(frames: readonly HistoryFrame[], cutoff: Cutoff | null | undefined, live?: NavModel | null): NavModel {
  return buildNavModelFromNodes(replayNodes(frames, cutoff, live));
}

/** The position of the slider (an index into the steps) for a change number: the last step at or before it. */
export function stepIndexFor(steps: readonly Step[], event: number | null): number {
  if (steps.length === 0) return 0;
  if (event === null) return steps.length - 1;
  let index = 0;
  for (let i = 0; i < steps.length; i += 1) {
    if ((steps[i] as Step).event <= event) index = i;
    else break;
  }
  return index;
}

/** Where a marker sits on the slider: the first step at or after the change it marks. */
export function markerIndex(steps: readonly Step[], marker: Pick<HistoryMarker, "event">): number {
  if (steps.length === 0) return 0;
  const index = steps.findIndex((step) => step.event >= marker.event);
  return index === -1 ? steps.length - 1 : index;
}

export const MARKER_WORDS: Record<HistoryMarker["kind"], string> = {
  hand_over: "Hand-over",
  sme_review: "SME review",
  restore: "Restore",
};

/** The names of the people behind the changes, once each, in alphabetical order. */
export function peopleIn(frames: readonly HistoryFrame[]): string[] {
  return Array.from(new Set(frames.map((frame) => frame.actor).filter((name) => !!name))).sort((a, b) => a.localeCompare(b));
}

/**
 * The elements a person has worked on up to the cut-off: the ones they changed, and the ones whose lines they
 * changed. A line's frame names its element as its parent.
 */
export function touchedBy(frames: readonly HistoryFrame[], person: string, cutoff: Cutoff | null | undefined): Set<Id> {
  const out = new Set<Id>();
  for (const frame of frames) {
    if (frame.actor !== person || !within(frame, cutoff)) continue;
    if (frame.subject_kind === "element") out.add(frame.subject_id);
    else if (frame.parent_id) out.add(frame.parent_id);
  }
  return out;
}
